import * as React from 'react'
import { useMemo, useState } from 'react'

import { Box, TableContainer } from '@mui/material'
// import { useScreenContext } from '../../../../Contexts/Modules/ScreenModule'
import { useUserContext } from '../../../Contexts/UserConfig'

//import { useLocalStorage } from '@uidotdev/usehooks' //FIXME: https://github.com/uidotdev/usehooks/issues/264
import { useLocalStorage } from 'usehooks-ts'
import { DataGridPro, GridRowHeightParams } from '@mui/x-data-grid-pro'
import PageSection from '../../../Constants&Theme/PageSection'
import {
  useGetScreenHoldings,
  useGetScreenSampleStatus,
} from '../../../APIs/Hooks/ScreenHooks'
import ScreenManageDGToolbar, { ScreenFilters } from './ScreenManageDGToolbar'
import ScreenManageAsyncToolbar from './ScreenManageAsyncToolbar'
import { screenSampleStatuses } from '../ManageCollections/ScreenManageRenderFunctions'
import { screenManageDGCols } from './ScreenManageDGCols'
import { useNavigate } from 'react-router-dom'
import { tsScreen } from '../../../Typescript/DB_Interface'
import { useStuartGet } from '../../../APIs/Hooks/PhpAPIHooks'

export interface ScreenManageStatusFilter {
  DFT: boolean
  PEN: boolean
  INP: boolean
  CPT: boolean
  ARC: boolean
}

function ScreenManage() {
  const navigate = useNavigate()
  const { loginConfig } = useUserContext()

  const [statusFilter, setStatusFilter] =
    useLocalStorage<ScreenManageStatusFilter>('collectionStatusFilter', {
      DFT: true,
      PEN: false,
      INP: false,
      CPT: true,
      ARC: false,
    })
  const statusOn = Object.entries(statusFilter).filter(([, value]) => value)
  const statusList = statusOn.map(([key]) => key).join(',')
  const [facilityID, setFacilityID] = useLocalStorage('facility', '1')
  const [dgFilters, setDGFilters] = useState<Partial<ScreenFilters>>({})

  const { data: screenData, isLoading: screensLoading } = useStuartGet(
    [
      '/screen/getScreens.php',
      `?FacilityID=${facilityID}&CollectionStatus=${statusList}`,
    ],
    !!facilityID && !!statusList,
  )
  const { data: holdings } = useGetScreenHoldings(facilityID, statusList)
  const { data: sampleStatus } = useGetScreenSampleStatus(
    facilityID,
    statusList,
  )

  const transformedScreens = useMemo(() => {
    if (screenData) {
      //merging holdings into one-to-many
      const merged: any = {}
      screenData.Screens.forEach((item: any) => {
        if (!merged?.[item.ScreenID]) {
          merged[item.ScreenID] = { ...item, HoldingID: [item.HoldingID] }
        } else {
          merged[item.ScreenID].HoldingID = [
            ...merged[item.ScreenID].HoldingID,
            item.HoldingID,
          ]
        }
      })
      //adding column for sample status values
      return Object.values(merged).map((row: any) => {
        return { ...row, SampleStatus: row.ScreenID }
      })
    }
    return []
  }, [screenData])

  const filteredScreens = useMemo(() => {
    let filtered: any[] = transformedScreens
    if (dgFilters?.ScreenID) {
      filtered = filtered.filter((f) =>
        f.ScreenID.toString().includes(dgFilters.ScreenID),
      )
    }
    if (dgFilters?.RequestName) {
      filtered = filtered.filter((f) =>
        f.Name.toLowerCase().includes(
          (dgFilters?.RequestName ?? '').toLowerCase(),
        ),
      )
    }
    if (dgFilters?.BillingStatus && dgFilters.BillingStatus.length > 0) {
      filtered = filtered.filter((f) =>
        dgFilters.BillingStatus!.includes(f.BillingStatus),
      )
    }
    if (dgFilters?.SampleStatus && dgFilters.SampleStatus.length > 0) {
      const setA: Set<string> = new Set(dgFilters.SampleStatus)
      filtered = filtered.filter((f) => {
        const setB: Set<string> = new Set(
          screenSampleStatuses(f.SampleStatus, sampleStatus).map(
            (s) => s.Status,
          ),
        )
        const intersect = [...setA].filter((x) => setB.has(x))
        return intersect.length > 0
      })
    }
    if (dgFilters?.Holdings && dgFilters.Holdings.length > 0) {
      const setA: Set<Number> = new Set(dgFilters.Holdings)
      filtered = filtered.filter((f) => {
        const setB: Set<Number> = new Set([...f.HoldingID])
        const intersect = [...setA].filter((x) => setB.has(x))
        return intersect.length > 0
      })
    }
    return filtered ?? []
  }, [transformedScreens, dgFilters, sampleStatus])

  return (
    <Box
      sx={{
        maxWidth: 'xl',
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <PageSection title={'Screen Requests'}>
        <ScreenManageAsyncToolbar
          facilityID={facilityID}
          setFacilityID={setFacilityID}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
        />
        <TableContainer
          sx={{
            width: 1,
          }}
        >
          <DataGridPro
            sx={{
              width: 1,
              '& .MuiDataGrid-cell': {
                alignItems: 'center',
                display: 'flex',
              },
            }}
            autoHeight
            loading={screensLoading}
            rows={filteredScreens}
            getRowId={(row) => row.ScreenID}
            slots={{
              toolbar: ScreenManageDGToolbar as any,
            }}
            slotProps={{
              toolbar: {
                dgFilters,
                setDGFilters,
              },
            }}
            onRowClick={({ row }) => {
              if (row.CollectionStatus === 'DFT') {
                navigate(`../screen/${row.ScreenID}/collections`)
              } else {
                navigate(`../screen/${row.ScreenID}`)
              }
            }}
            getRowHeight={({ id, densityFactor }: GridRowHeightParams) => {
              const screen = transformedScreens.filter(
                (s: tsScreen) => s.ScreenID === id,
              )[0]
              return (
                Math.max(
                  screen?.HoldingID.length,
                  screenSampleStatuses(id as number, sampleStatus).length,
                ) *
                  40 *
                  densityFactor +
                10
              )
            }}
            columns={screenManageDGCols(loginConfig, sampleStatus, holdings)}
            pagination
            initialState={{
              sorting: {
                sortModel: [{ field: 'CollectionStatus', sort: 'asc' }],
              },
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            pageSizeOptions={[10]}
          />
        </TableContainer>
      </PageSection>
    </Box>
  )
}

export default ScreenManage
